import React, {useRef, useState} from 'react'
import {
  Button,
  DateTimeRangePicker,
  Loading,
  Modal,
  Select,
} from '@therms/atalaya'
import {
  Alerts,
  isWholeDayTimeOff,
  TIME_OFF_TYPE_OPTIONS,
  timeOffDraftRange,
  timeOffEditDays,
  TimeOffRequest,
  TimeOffRequestType,
} from '@therms/react-modules'
import dayjs from 'dayjs'

export interface TimeOffDraft {
  endTime: string
  startTime: string
  type: TimeOffRequestType
}

type Range = {end?: string; start?: string}

interface RequestTimeOffModalProps {
  closeHandler: () => void
  onSubmit: (draft: TimeOffDraft) => Promise<void>
  /** The pending request being edited; left out to file a new one. */
  request?: TimeOffRequest
}

function initialTimes(request?: TimeOffRequest): Range {
  if (!request || isWholeDayTimeOff(request.startTime, request.endTime))
    return {}
  return {end: request.endTime, start: request.startTime}
}

export function RequestTimeOffModal({
  closeHandler,
  onSubmit,
  request,
}: RequestTimeOffModalProps) {
  const [loading, setLoading] = useState(false)
  // `loading` only disables the button on the next render; a fast double
  // click would file twice (with approval off, both are approved).
  const submitting = useRef(false)
  // The picker keeps the last valid range when it rejects an edit, while
  // showing its error; without this, Submit would file the old range.
  const [timesRejected, setTimesRejected] = useState(false)
  const [days, setDays] = useState<Range>(() => timeOffEditDays(request))
  const [times, setTimes] = useState<Range>(() => initialTimes(request))
  const [withTimes, setWithTimes] = useState(
    () => !!request && !isWholeDayTimeOff(request.startTime, request.endTime),
  )
  const [type, setType] = useState<TimeOffRequestType>(
    request?.type ?? 'vacation',
  )

  const drafted =
    withTimes && timesRejected
      ? undefined
      : timeOffDraftRange({days, times, withTimes})
  // No past days for now (Tom, 2026-10-23); today is fine. Also stops an old
  // pending request being saved with its past dates unchanged.
  const range =
    drafted && Date.parse(drafted.startTime) >= dayjs().startOf('day').valueOf()
      ? drafted
      : undefined

  function switchToTimes() {
    // Start from the first picked day, office hours, as most requests are part days.
    if (days.start && !times.start) {
      const day = dayjs(days.start).startOf('day')
      setTimes({
        end: day.hour(17).toISOString(),
        start: day.hour(9).toISOString(),
      })
    }
    setWithTimes(true)
  }

  async function handleSubmit() {
    if (!range || submitting.current) return
    submitting.current = true
    setLoading(true)
    try {
      await onSubmit({
        ...range,
        type,
      })
      closeHandler()
    } catch (err: any) {
      Alerts.error(
        request
          ? 'There was a problem updating your request'
          : 'There was a problem requesting time off',
        err.message,
      )
    } finally {
      submitting.current = false
      setLoading(false)
    }
  }

  return (
    <Modal
      closeHandler={closeHandler}
      header={request ? 'Edit time off request' : 'Request time off'}
      width="md"
    >
      <div className="gap-base relative flex flex-col">
        {loading && <Loading overlay size="lg" />}

        <div className="gap-xxs flex flex-col">
          <span className="text-color-subtle text-xs font-semibold uppercase opacity-80">
            Type
          </span>
          <div className="w-[180px]">
            <Select
              isSearchable={false}
              onChangeValue={(value) => setType(value as TimeOffRequestType)}
              options={TIME_OFF_TYPE_OPTIONS}
              value={type}
            />
          </div>
        </div>

        <div className="gap-xxs flex flex-col">
          <div className="gap-sm flex items-center">
            <span className="text-color-subtle text-xs font-semibold uppercase opacity-80">
              {withTimes ? 'From and to' : 'Days'}
            </span>
            <div className="flex-1" />
            <Button
              onClick={() => {
                // The picker remounts without its error, so drop the flag too.
                setTimesRejected(false)
                if (withTimes) setWithTimes(false)
                else switchToTimes()
              }}
              size="xs"
              subtle
              variant="main"
            >
              {withTimes ? 'Whole days' : 'Set times'}
            </Button>
          </div>
          {withTimes ? (
            <DateTimeRangePicker
              invalidRangeMessage="The end must be after the start."
              minuteStep={15}
              disableDatesBeforeDate="today"
              mode="datetime"
              onChangeValue={(value) => {
                setTimesRejected(false)
                setTimes(value)
              }}
              onChangeValueBehavior="StartOrEnd"
              onInvalidRange={() => setTimesRejected(true)}
              value={times}
            />
          ) : (
            <DateTimeRangePicker
              disableDatesBeforeDate="today"
              mode="date"
              onChangeValue={setDays}
              onChangeValueBehavior="StartOrEnd"
              value={days}
            />
          )}
        </div>

        <div className="gap-sm flex items-center justify-end">
          <Button
            disabled={loading}
            onClick={closeHandler}
            outline
            variant="neutral"
          >
            Cancel
          </Button>
          <Button
            disabled={loading || !range}
            onClick={handleSubmit}
            variant="main"
          >
            {request ? 'Save changes' : 'Submit request'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
